import React, { ChangeEvent, useCallback, useEffect, useState, useMemo } from 'react';
import {
	Alert,
	Backdrop,
	CircularProgress,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { largePageSizeOptions } from 'constants/constants';

import { extractErrorMessage } from 'features/quotation/helpers';
import { PageNoResult } from 'features/common/components/PageNoResults';
import { ExportToExcelButton } from 'features/common/components/ExportToExcelButton';
import { ExportExcelOptions, exportToExcel } from 'features/common/helpers';
import { ShowAlertState } from 'features/common/types';

import {
	getFlightAndAncillaryIssueSummariesList,
	getFlightAndAncillaryIssueSummariesListToExport,
} from '../../services';
import { IFlightAndFlightAncillaryIssueSummary } from '../../types';
import { flightAndAncillaryIssueSummaryListStyles } from './styles';
import { flightAndAncillaryIssueSummaryListHeaderCells, transformValue } from './constants';
import { FlightIssuingControlCard } from '../FlightIssuingControlCard';
import { ModernFilters, ModernFiltersState } from './ModernFilters';
import { SortSelector, SortConfig } from './SortSelector';
import { LocalFilters, LocalFiltersState } from './LocalFilters';
import { convertModernFiltersToEndpoint, getDefaultSortConfig } from './ModernFilters/helpers';

export const FlightAndAncillaryIssueSummaryList = (): JSX.Element => {
	const classes = flightAndAncillaryIssueSummaryListStyles();
	const [loading, setLoading] = useState<boolean>(false);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortConfig, setSortConfig] = useState<SortConfig>(getDefaultSortConfig());
	const [currentSize, setCurrentSize] = useState<number>(30);
	const [pageTotal, setPageTotal] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	// Filtros por defecto para el componente principal
	const defaultFiltersForList: ModernFiltersState = {
		vcCode: '',
		saleOrderId: '',
		saleOrderCreatedBy: '',
		saleOrderDealId: '',
		ticketType: '',
		ticketingRequested: true, // Por defecto true
		ticketingStatus: 'pending', // Por defecto pending
		saleOrderAgencyId: '',
		serviceEnable: true, // Por defecto true (solo habilitados)
		ticketingLimitDateFrom: null,
		ticketingLimitDateTo: null,
		serviceCreatedFrom: null,
		serviceCreatedTo: null,
	};

	const [modernFilters, setModernFilters] = useState<ModernFiltersState>(defaultFiltersForList);
	const [summaryList, setSummaryList] = useState<IFlightAndFlightAncillaryIssueSummary[] | null>([]);
	const [localFilters, setLocalFilters] = useState<LocalFiltersState>({
		workflows: [],
		stages: [],
		agents: [],
	});
	const [searchValue, setSearchValue] = useState<string>('');

	const handleFiltersChange = useCallback((filters: ModernFiltersState) => {
		setModernFilters(filters);
		setCurrentPage(1); // Reset a primera página cuando cambien los filtros
	}, []);

	const handleClearFilters = useCallback(() => {
		setCurrentPage(1);
	}, []);

	const handleSortChange = (newSort: SortConfig) => {
		setSortConfig(newSort);
		setCurrentPage(1); // Reset a primera página cuando cambie el sort
	};

	const handleLocalFiltersChange = useCallback((filters: LocalFiltersState) => {
		setLocalFilters(filters);
	}, []);

	// Filtrar datos localmente basado en los filtros de pantalla
	const filteredSummaryList = useMemo(() => {
		if (!summaryList || summaryList.length === 0) return summaryList;

		const hasLocalFilters =
			localFilters.workflows.length > 0 || localFilters.stages.length > 0 || localFilters.agents.length > 0;

		if (!hasLocalFilters) return summaryList;

		return summaryList.filter((item) => {
			// Filtro por workflow
			if (localFilters.workflows.length > 0) {
				const itemWorkflow = item.crmWorkflowName?.trim();
				if (!itemWorkflow || !localFilters.workflows.includes(itemWorkflow)) {
					return false;
				}
			}

			// Filtro por etapa
			if (localFilters.stages.length > 0) {
				const itemStage = item.crmSteepName?.trim();
				if (!itemStage || !localFilters.stages.includes(itemStage)) {
					return false;
				}
			}

			// Filtro por agente
			if (localFilters.agents.length > 0) {
				const itemAgent = item.crmAgentCompleteName?.trim();
				if (!itemAgent || !localFilters.agents.includes(itemAgent)) {
					return false;
				}
			}

			return true;
		});
	}, [summaryList, localFilters]);

	const loadSummaryList = useCallback(async () => {
		try {
			setLoading(true);
			// Limpiar la lista antes de cargar nuevos datos
			setSummaryList([]);

			const endpointFilters = convertModernFiltersToEndpoint(modernFilters);

			// Convertir sortConfig al formato que espera el API
			const apiSortFields = [{ field: sortConfig.field, order: sortConfig.order }];
			const results = await getFlightAndAncillaryIssueSummariesList(
				currentPage,
				currentSize,
				apiSortFields,
				endpointFilters,
				searchValue,
			);

			setSummaryList(results.data.elements || []);
			setPageTotal(Math.ceil(results.data.total / currentSize));
			setTotalCount(results.data.total);
			setLoading(false);
		} catch (error) {
			// Limpiar la lista en caso de error también
			setSummaryList([]);
			setPageTotal(0);
			setTotalCount(0);
			setAlert({ show: true, severity: 'error', message: 'Error al cargar los datos.' });
			setLoading(false);
		}
	}, [currentPage, currentSize, sortConfig, modernFilters, searchValue]);

	useEffect(() => {
		loadSummaryList();
	}, [loadSummaryList]);

	const handleExportToExcel = async () => {
		try {
			setLoading(true);
			const title = 'Control de Emisión de Vuelos';
			const endpointFilters = convertModernFiltersToEndpoint(modernFilters);
			// Convertir sortConfig al formato que espera el API
			const apiSortFields = [{ field: sortConfig.field, order: sortConfig.order }];
			const envelopedTransactionList = (
				await getFlightAndAncillaryIssueSummariesListToExport(apiSortFields, endpointFilters)
			).data;
			const listToExport = envelopedTransactionList.elements;
			const optionsToExport = {
				title: title,
				headers: flightAndAncillaryIssueSummaryListHeaderCells,
				widthColumns: [20, 20, 15, 15, 20, 25, 20, 20, 20, 25, 20, 20, 15, 30, 25, 25, 25],
				filename: title,
			} as ExportExcelOptions;
			exportToExcel(listToExport, optionsToExport, transformValue);
			setLoading(false);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al exportar el listado'),
			});
			setLoading(false);
		}
	};

	const handleSearch = () => {
		setCurrentPage(1);
		// El useEffect se encargará de recargar cuando cambien las dependencias
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(event.target.value);
	};

	const handleCloseAlert = () => {
		setAlert(defaultAlertState);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};
	const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
		setCurrentPage(page);
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={loading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid
				container
				spacing={1}
				sx={{
					justifyContent: 'center',
				}}
			>
				{/* Barra de búsqueda y exportación */}
				<Grid container spacing={2} sx={{ maxWidth: '90vw' }} justifyContent="space-between" alignItems="center">
					<Grid item xs={12} md="auto" className={classes.containerButtons} gap={1}>
						<Tooltip title="Buscá por Service ID, Sale Order ID, PNR, Pasajero Principal, Deal ID o Agente CRM">
							<TextField
								label="Buscar"
								variant="outlined"
								size="small"
								value={searchValue}
								onChange={handleChange}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={handleSearch}>
												<SearchIcon />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</Tooltip>
					</Grid>
					<Grid item xs={12} md="auto" sx={{ pl: 2 }}>
						<ExportToExcelButton
							disabled={filteredSummaryList?.length == 0}
							handleExportToExcel={handleExportToExcel}
						/>
					</Grid>
				</Grid>

				{/* Filtros modernos */}
				<Grid container spacing={2} sx={{ maxWidth: '90vw' }}>
					<Grid item xs={12}>
						<ModernFilters
							onFiltersChange={handleFiltersChange}
							onClearFilters={handleClearFilters}
							initialFilters={defaultFiltersForList}
						/>
					</Grid>
				</Grid>

				{/* Selector de ordenamiento */}
				<Grid container spacing={2} sx={{ maxWidth: '90vw' }}>
					<Grid item xs={12}>
						<SortSelector currentSort={sortConfig} onSortChange={handleSortChange} />
					</Grid>
				</Grid>

				{/* Filtros locales de pantalla */}
				<Grid container spacing={2} sx={{ maxWidth: '90vw' }}>
					<Grid item xs={12}>
						<LocalFilters data={summaryList || []} onFiltersChange={handleLocalFiltersChange} />
					</Grid>
				</Grid>

				<Grid item xs={12} sx={{ m: 5, justifyContent: 'flex-start', ml: 19 }}>
					<Typography variant="subtitle1">
						En pantalla: {filteredSummaryList?.length} resultados de {totalCount}
						{filteredSummaryList?.length !== summaryList?.length && (
							<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
								(filtrados de {summaryList?.length} cargados)
							</Typography>
						)}
					</Typography>
				</Grid>

				{/* Cards Container */}
				<Grid container spacing={2} sx={{ maxWidth: '90vw', px: 2 }}>
					{filteredSummaryList?.length === 0 ? (
						<Grid item xs={12}>
							<PageNoResult />
						</Grid>
					) : (
						filteredSummaryList?.map((item: IFlightAndFlightAncillaryIssueSummary) => (
							<Grid item xs={12} key={item.serviceId}>
								<FlightIssuingControlCard item={item} />
							</Grid>
						))
					)}
				</Grid>
				<Grid item xs={12}>
					<Grid container justifyContent="center" flexDirection="row" padding={2}>
						<Grid item xs={12} md={1} textAlign="center">
							<Select value={currentSize} onChange={handleRowsPerPageChange}>
								{largePageSizeOptions.map((value) => (
									<MenuItem key={value} value={value}>
										{value}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={12} md={6} marginTop={1}>
							<Pagination count={pageTotal} page={currentPage} onChange={handlePageChange} color="primary" />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Snackbar open={alert.show} autoHideDuration={2000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
